import { Stack } from "@mui/material";

import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { AssignStatus } from "@/enum/list.enum";

import { LiEventEntry } from "../../../LiEvent/schema/liEventEntry";
import { ListPerson } from "../../schema/list";

import {
	renderAboutColumn,
	renderAssignToColumn,
	renderCompanyCityColumn,
	renderCompanyColumn,
	renderCompanyCountryColumn,
	renderCompanyPeopleCheckColumn,
	renderCompanyStateColumn,
	renderConnectionsColumn,
	renderEstimatedNumEmployeesColumn,
	renderEventColumn,
	renderFollowers,
	renderFoundedYearColumn,
	renderGenderColumn,
	renderHeadlineColumn,
	renderIndustryColumn,
	renderLanguageColumn,
	renderLiEventAttendeesColumn,
	renderLiEventDateColumn,
	renderNameColumn,
	renderPersonCityColumn,
	renderPersonCountryColumn,
	renderPersonStateColumn,
	renderSelectColumn,
	renderSeniorityColumn,
	renderSourceNameColumn,
	renderTitleColumn,
	renderCreatedAt,
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderExclusionReasonColumn,
	renderPreviousEmployersColumn,
	renderCompanyCategoryColumn,
	renderCompanyScoreColumn,
	renderPersonaCategoryColumn,
	renderPersonaScoreColumn,
	renderReportetEmployeesColumn,
	renderRoleChangeColumn,
	renderPreviousEmployerColumn,
	renderPreviousJobTitleColumn,
	renderCampaignAddedAt,
	renderAssignedStatusColumn,
	renderLastNodeTypeColumn,
	renderLastScheduledAtColumn,
	renderLastMessageColumn,
	renderDeepResearchStatusColumn,
} from "../../utils/allColumns";
import {
	renderCompanyOtherListsColumn,
	renderOtherListsColumn,
} from "../../utils/otherListsColumns";
import { renderMaybeColumn } from "../../utils/renderMaybeColumn";
import { GetIrlColumns } from "../CustomerImplementations/IrlColumns";

export interface LiEventTableColumnsProps {
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: ({
		id,
		name,
		columnConfigId,
	}: {
		id: number;
		name: string;
		columnConfigId?: number;
	}) => void;
	showEdit: { id: number; name: string; columnConfigId?: number } | undefined;
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus: AssignStatus;
	onResearchClick?: (row: ListPerson) => void;
	onOpenDetail?: (row: ListPerson) => void;
}

export const LiEventTableColumns = ({
	selectAll,
	setShowAll,
	toggleSelectAll,
	selectedItems,
	handleSelectItem,
	handleCopyClick,
	ts,
	sortingInbox,
	setShowEdit,
	showEdit,
	integrations,
	handleSelectCampaignAndProspect,
	assignStatus,
	onResearchClick,
	onOpenDetail,
}: LiEventTableColumnsProps): TableColumn[] => {
	const columns = [
		renderSelectColumn(setShowAll, toggleSelectAll, selectAll, selectedItems, handleSelectItem),
		renderMaybeColumn(ts),
		renderAssignToColumn(ts, handleCopyClick, handleSelectCampaignAndProspect),
		renderNameColumn(ts, handleCopyClick, sortingInbox, onOpenDetail),
		renderCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderExclusionReasonColumn(ts, sortingInbox),
		renderDeepResearchStatusColumn(onResearchClick),
		renderCompanyPeopleCheckColumn(ts),
		renderGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderSalutationDeColumn(ts),
		renderSalutationEnColumn(ts),
		renderTitleColumn(ts, handleCopyClick, sortingInbox),
		renderHeadlineColumn(ts, handleCopyClick, sortingInbox),
		renderAboutColumn(ts, handleCopyClick, sortingInbox),
		renderSeniorityColumn(ts, handleCopyClick, sortingInbox),
		renderEventColumn(ts, handleCopyClick, sortingInbox),
		renderLiEventAttendeesColumn(ts, handleCopyClick, sortingInbox),
		renderLiEventDateColumn(ts, handleCopyClick, sortingInbox),
		renderConnectionsColumn(ts, sortingInbox),
		renderIndustryColumn(ts, handleCopyClick, sortingInbox),
		renderEstimatedNumEmployeesColumn(ts, handleCopyClick, sortingInbox),
		renderReportetEmployeesColumn(ts, handleCopyClick, sortingInbox),
		renderFoundedYearColumn(ts, handleCopyClick, sortingInbox),
		renderPersonCityColumn(ts, handleCopyClick, sortingInbox),
		renderPersonStateColumn(ts, handleCopyClick, sortingInbox),
		renderPersonCountryColumn(ts, handleCopyClick, sortingInbox),
		renderCompanyCityColumn(ts, handleCopyClick, sortingInbox),
		renderCompanyStateColumn(ts, handleCopyClick, sortingInbox),
		renderCompanyCountryColumn(ts, handleCopyClick, sortingInbox),
		renderLanguageColumn(ts, sortingInbox),
		renderOtherListsColumn(ts, sortingInbox),
		renderCompanyOtherListsColumn(ts, sortingInbox),
		renderFollowers(ts, sortingInbox),
		{
			title: ts("otherEvents"),
			width: "fit-content",
			uid: "otherEvents",
			hideable: true,
			moveable: true,
			show: true,
			render: (row: ListPerson) => (
				<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
					<Stack alignItems="flex-start" direction="column" justifyContent="flex-start">
						<div>
							{row.otherEvents?.map((liEventEntry: LiEventEntry) => liEventEntry.name)?.join(",")}
						</div>
					</Stack>
				</Stack>
			),
		},
		renderSourceNameColumn(ts, handleCopyClick, sortingInbox),
		renderCreatedAt(ts, sortingInbox),
		renderPreviousEmployersColumn(ts),
		renderPreviousEmployerColumn(ts),
		renderPreviousJobTitleColumn(ts),
		renderRoleChangeColumn(),
	];

	if (assignStatus === AssignStatus.ASSIGNED) {
		columns.push(renderCampaignAddedAt(ts, sortingInbox));
		columns.push(renderAssignedStatusColumn(ts, sortingInbox));
		columns.push(renderLastNodeTypeColumn(ts, sortingInbox));
		columns.push(renderLastScheduledAtColumn(ts, sortingInbox));
		columns.push(renderLastMessageColumn(ts));
	}

	if (integrations.LEAD_SCORING) {
		columns.push(renderPersonaCategoryColumn(sortingInbox));
		columns.push(renderCompanyCategoryColumn(sortingInbox));
		columns.push(renderPersonaScoreColumn(sortingInbox));
		columns.push(renderCompanyScoreColumn(sortingInbox));
	}

	if (integrations.IRL_COUNTRIES_SUM) {
		const irlColumns = GetIrlColumns(ts);

		columns.push(...irlColumns);
	}

	return columns;
};
